import axios from 'axios';
import * as system from "../constants/systemConstants";
import {errorResponse} from "../utilities/errorResponse";
import store from "../store/store";
import {getToken, setToken} from "../actions/loginActions";
import {FormatPageableAndFilter} from "../utilities/PageableAndFilterUtilities";
import {msalInstance} from "../authProvider";
import { validateTokenAuthorities, createUnauthorizedMessage } from "../utilities/jwtUtils";
import {debugLog, debugWarn} from "../utilities/debugLogger";


// Feature flag: prefer using access token instead of id token for API calls.
const USE_ACCESS_TOKEN = true; // Toggle if backend strictly expects id token

let lastStoredToken = null; // Guard against redundant writes
let inflightAcquirePromise = null; // Single-flight token acquisition

export const axiosWrap = function (method, url, data = null, options = {}) {
    const accessTokenRequest = {
        scopes: ["user.read"],
        account: msalInstance.getActiveAccount(),
    };

    // Add timestamp to all requests
    const timestamp = new Date().toISOString();
    const timestampedData = data ? 
        (typeof data === 'object' ? { ...data, requestTimestamp: timestamp } : data) :
        { requestTimestamp: timestamp };

    // Define the default axios config
    const defaultConfig = {
        url: url,
        method: method,
        headers: {
            "Content-Type": "application/json;charset=UTF-8",
            "X-Request-Timestamp": timestamp, // Also add to headers for non-JSON requests
        },
        ...options,
    };

    // Use timestamped data for requests that support body
    if (data || ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
        defaultConfig.data = timestampedData;
    }

    // Try to initialize msalInstance and acquire a token
    debugLog('HTTP',`Request ${method.toUpperCase()} ${url}`);
    const performRequest = () => {
        const authorizationToken = getToken();
        if (authorizationToken) {
            defaultConfig.headers["Authorization"] = "Bearer " + authorizationToken;
        }
        const start = Date.now();
        return axios.request(defaultConfig).then(resp => {
            debugLog('HTTP',`Response ${resp.status} ${method.toUpperCase()} ${url} in ${Date.now()-start}ms`);
            return resp;
        }).catch(err => {
            debugWarn('HTTP',`Request error for ${method.toUpperCase()} ${url}`, {status: err?.response?.status});
            errorResponse(err, store.dispatch);
            throw err;
        });
    };

    // Acquire token single-flight
    const acquire = () => {
        if (!inflightAcquirePromise) {
            inflightAcquirePromise = msalInstance.initialize()
                .then(() => {
                    debugLog('AUTH','MSAL initialized (single-flight)');
                    return msalInstance.acquireTokenSilent(accessTokenRequest);
                })
                .catch(e => {
                    debugWarn('AUTH','Silent acquire failed pre-token', {message: e?.message, code: e?.errorCode});
                    throw e;
                })
                .finally(() => { inflightAcquirePromise = null; });
        }
        return inflightAcquirePromise;
    };

    return acquire()
        .then(accessTokenResponse => {
            // Prefer access token; fallback to id token if validation fails or flag disabled.
            let candidateToken = null;
            let usedIdToken = false;
            if (USE_ACCESS_TOKEN) {
                candidateToken = accessTokenResponse.accessToken;
                if (!validateTokenAuthorities(candidateToken)) {
                    debugWarn('AUTH','Access token authorities invalid; attempting id token fallback');
                    candidateToken = accessTokenResponse.idToken;
                    usedIdToken = true;
                }
            } else {
                candidateToken = accessTokenResponse.idToken;
                usedIdToken = true;
            }

            if (!validateTokenAuthorities(candidateToken)) {
                debugWarn('AUTH','Selected token invalid authorities, aborting');
                throw new Error(createUnauthorizedMessage(candidateToken));
            }

            // Guard redundant writes
            if (candidateToken && candidateToken !== lastStoredToken) {
                setToken(candidateToken);
                lastStoredToken = candidateToken;
                debugLog('AUTH',`Stored new ${usedIdToken? 'id':'access'} token (length=${candidateToken.length})`);
            } else {
                debugLog('AUTH','Reusing existing token (no change)');
            }

            return performRequest();
        })
        .catch(err => {
            // Fallback path: use whatever token we already have and proceed
            debugWarn('AUTH','Acquire path failed, using existing stored token', {message: err?.message, code: err?.errorCode});
            return performRequest();
        });
};



const GET = "get";
const PUT = "put";
const POST = "post";
const DELETE = "delete";

// converts UI column name to database equivalent
const columnNameMapper = {
    "Name": "name",
    "Qualifier": "nameQualifier",
    "Last updated": "lastModifiedDate",
    "Updated by": "lastModifiedBy",
    "Type": "questionType",
    "Start date": "startDate",
    "End date": "endDate",
    "Outstanding": "outstandingFlag"
};

const columnNameMapper2 = {
    "Name": "name",
    "Qualifier": "nameQualifier",
    "Last updated": "lastModifiedDate",
    "Updated by": "lastModifiedBy",
    "Type": "type",
    "Start date": "startDate",
    "End date": "endDate",
    "Outstanding": "outstandingFlag"
};

const templateApiMapper = {
    "questionnaire": "questionnaire-templates",
    "category": "question-category-templates",
    "group": "question-group-templates",
    "question": "question-templates",
    "action": "questionnaire-action-templates",
    "tag": "search-tags",
    "integration": "integrations",
    "integrationLogs": "integrations/logs",
};


class TemplateApi {
    static getAuthorizationToken(username, password, rememberMe) {
        let data = {
            username,
            password,
            rememberMe
        };

        data = JSON.stringify(data);

        return axiosWrap(POST, system.QD_API_AUTH_URL, data)
            .then((response) => {
                return response.data; //data is in the form of {"id_token": x}
            });
    }

    // -----------------------------------------------------------------------------------------------------------------
    // Questionnaires !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! <editor-fold>
    // -----------------------------------------------------------------------------------------------------------------

    static getCurrentQuestionnaire(questionnaireKey) {
        return axiosWrap(GET,
            system.QD_API_URL +
            "questionnaire-templates/" +
            questionnaireKey +
            "/current")
            .then((response) => {
                return response.data;
            });
    }


    static getQuestionnaireById(id) {
        return axiosWrap(GET,
            system.QD_API_URL +
            "questionnaire-templates/" +
            id)
            .then((response) => {
                return response.data;
            });
    }


    static createQuestionnaireTemplate(questionnaireTemplate) {
        return axiosWrap(POST, system.QD_API_URL +
            "questionnaire-templates", questionnaireTemplate)
            .then((response) => {
                    return response.data;
                }
            );
    }


    static updateQuestionnaireTemplate(questionnaireTemplate) {
        return axiosWrap(PUT,
            system.QD_API_URL +
            "questionnaire-templates/" +
            questionnaireTemplate.templateKey, questionnaireTemplate)
            .then((response) => {
                return response.data;
            });
    }


    static cloneQuestionnaireTemplate(template) {
        return axiosWrap(PUT,
            system.QD_API_URL + "questionnaire-templates/" + template.id + "/clone", { "nameQualifier": template.nameQualifier, "notes": template.notes})
            .then((response) => {
                return response.data;
            });
    }


    static checkActionNameExists(filter) {
        let url = `${system.QD_API_URL}questionnaire-action-templates/exists?`;
        if (filter.templateKey) {
            url = url + `templateKey=${filter.templateKey}&`;
        }
        if (filter.name) {
            url = url + `exactName=${filter.name}&`;
        }

        return axiosWrap(GET, url)
            .then((response) => {
                return response.data;
            });
    }


    // static checkQuestionnaireExists(filter) {
    //     let url = `${system.QD_API_URL}questionnaire-templates/exists?`;
    //     if (filter.templateKey) {
    //         url = url + `templateKey=${filter.templateKey}&`;
    //     }
    //     if (filter.name) {
    //         url = url + encodeURI(`exactName=${filter.name}&`);
    //     }
    //
    //     return axiosWrap(GET, url)
    //         .then((response) => {
    //             return response.data;
    //         });
    // }


    static checkQuestionnaireNameQualifierExists(nameQualifier, id) {
        let url = `${system.QD_API_URL}questionnaire-templates/${id}/clone/validate?nameQualifier=${nameQualifier}`;

        return axiosWrap(GET, url)
            .then((response) => {
                return response.data;
            });
    }


    static publishQuestionnaire(id, publishDTO) {
        return axiosWrap(PUT, system.QD_API_URL + `questionnaire-templates/${id}/publish`, publishDTO);
    }


    static retireQuestionnaire(id, retireDTO) {
        return axiosWrap(PUT, system.QD_API_URL + `questionnaire-templates/${id}/retire`, retireDTO);
    }


    static deleteQuestionnaire(id, deleteDTO) {
        return axiosWrap(DELETE, system.QD_API_URL + `questionnaire-templates/${id}`, deleteDTO);
    }


    static getQuestionnaireGraph(id) {
        return axiosWrap(GET, system.QD_API_URL + `questionnaire-templates/${id}/graph`)
            .then((response) => {
                return response.data;
            });
    }

    static getCategoryGraph(id) {
        return axiosWrap(GET, system.QD_API_URL + `question-category-templates/${id}/graph`)
            .then((response) => {
                return response.data;
            });
    }

    // getCategoryGraphWithKey will use the KEY to get the current category based on KEY and convert to a graph by ID
    // This will be used by questionnaire editor
    static getCategoryGraphWithKey(key) {
        return axiosWrap(GET, system.QD_API_URL + `question-category-templates/${key}/graph`)
            .then((response) => {
                return response.data;
            });
    }

    static getGroupGraph(id) {
        return axiosWrap(GET, system.QD_API_URL + `question-group-templates/${id}/graph`)
            .then((response) => {
                return response.data;
            });
    }

    // getGroupGraphWithKey will use the KEY to get the current group based on KEY and convert to a graph by ID
    // This will be used by category editor
    static getGroupGraphWithKey(key) {
        return axiosWrap(GET, system.QD_API_URL + `question-group-templates/${key}/graph`)
            .then((response) => {
                return response.data;
            });
    }


    static changeQuestionnaireDates(id, changeDateDTO) {
        return axiosWrap(PUT, system.QD_API_URL + `questionnaire-templates/${id}/changeEffectiveDates`, changeDateDTO);
    }


    static getOutstandingQuestionnaireTemplateCount(){
        return axiosWrap(GET, system.QD_API_URL + `questionnaire-templates/count-outstanding`)
            .then((response) => {
                return response.data;
            });
    }


    static getQuestionnaireReference(jsonObj) {
        return axiosWrap(POST, system.QD_API_URL +
            "questionnaire-reference", jsonObj)
            .then((response) => {
                    return response.data;
                }
            );
    }

    static getMetadataRequiredTokens(templateID) {
        return axiosWrap(GET, system.QD_API_URL + `questionnaire-reference/configuredTokens/${templateID}`).then(response => {
            return response.data
        })
    }

    static updateQuestionnaireMetadata(jsonObj, templateID) {
        return axiosWrap(PUT, system.QD_API_URL + `questionnaire-templates/${templateID}/testData`, jsonObj)
    }

    static getQuestionnaireVersions(templateKey) {
        return axiosWrap(GET, system.QD_API_URL + `questionnaire-templates/${templateKey}/history`).then(response => response.data.collection)
    }

    //</editor-fold>
    // -----------------------------------------------------------------------------------------------------------------


    // -----------------------------------------------------------------------------------------------------------------
    // Categories !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! <editor-fold>
    // -----------------------------------------------------------------------------------------------------------------


    // Get the current category for specified templateKey
    static getCurrentCategory(templateKey) {
        return axiosWrap(GET, system.QD_API_URL + "question-category-templates/" + templateKey + "/current")
            .then((response) => {
                return response.data;
            });
    }


    static getCategoryById(id) {
        return axiosWrap(GET,
            system.QD_API_URL +
            "question-category-templates/" +
            id)
            .then((response) => {
                return response.data;
            });
    }


    static createCategoryTemplate(categoryTemplate) {
        return axiosWrap(POST,system.QD_API_URL +
            "question-category-templates", categoryTemplate)
            .then((response) => {
                    return response.data;
                }
            );
    }


    static updateCategoryTemplate(categoryTemplate) {
        return axiosWrap(PUT,
            system.QD_API_URL +
            "question-category-templates/" +
            categoryTemplate.templateKey, categoryTemplate)
            .then((response) => {
                return response.data;
            });
    }



    //</editor-fold>
    // -----------------------------------------------------------------------------------------------------------------

    // -----------------------------------------------------------------------------------------------------------------
    // Groups !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! <editor-fold>
    // -----------------------------------------------------------------------------------------------------------------

    static getGroupById(id) {
        return axiosWrap(GET,
            system.QD_API_URL +
            "question-group-templates/" +
            id)
            .then((response) => {
                return response.data;
            });
    }


    // Get the current group for specified templateKey
    static getCurrentGroup(templateKey) {
        return axiosWrap(GET, system.QD_API_URL + "question-group-templates/" + templateKey + "/current")
            .then((response) => {
                return response.data;
            });
    }


    static createGroupTemplate(groupTemplate) {
        return axiosWrap(POST,
            system.QD_API_URL +
            "question-group-templates/", groupTemplate)
            .then((response) => {
                    return response.data;
                }
            );
    }


    static saveGroupTemplate(groupTemplate) {
        return axiosWrap(PUT,
            system.QD_API_URL +
            "question-group-templates/" +
            groupTemplate.templateKey, groupTemplate)
            .then((response) => {
                    return response.data;
                }
            );
    }

    //</editor-fold>
    // -----------------------------------------------------------------------------------------------------------------

    // -----------------------------------------------------------------------------------------------------------------
    // Questions !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! <editor-fold>
    // -----------------------------------------------------------------------------------------------------------------


    // Get the current group for specified templateKey
    static getCurrentQuestion(templateKey) {
        return axiosWrap(GET, system.QD_API_URL + "question-templates/" + templateKey + "/current")
            .then((response) => {
                return response.data;
            });
    }

    // Get the specific group by ID
    static getQuestionById(id) {
        return axiosWrap(GET,
            system.QD_API_URL +
            "question-templates/" +
            id)
            .then((response) => {
                return response.data;
            });
    }


    static getQuestionnaireList() {
        return axiosWrap(GET,system.QD_API_URL +
            "questionnaire-templates/latest")
            .then((response) => {
                return response.data;
            });
    }


    static saveQuestionTemplate(questionTemplate) {
        return axiosWrap(PUT, system.QD_API_URL + "question-templates/" + questionTemplate.templateKey, questionTemplate)
            .then((response) => {
                return response.data;
            });
    }


    static saveQuestionTemplateTags(questionTemplate) {
        return axiosWrap(PUT, system.QD_API_URL + "question-templates/tags/" + questionTemplate.templateKey, questionTemplate)
            .then((response) => {
                return response.data;
            });
    }


    static createQuestionTemplate(questionTemplate) {
        return axiosWrap(POST, system.QD_API_URL + "question-templates", questionTemplate)
            .then((response) => {
                return response.data;
            });
    }


    //</editor-fold>
    // -----------------------------------------------------------------------------------------------------------------

    // -----------------------------------------------------------------------------------------------------------------
    // Actions !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! <editor-fold>
    // -----------------------------------------------------------------------------------------------------------------

    static getActionById(id) {
        return axiosWrap(GET,
            system.QD_API_URL + "questionnaire-action-templates/" + id)
            .then((response) => {
                return response.data;
            });
    }


    static getCurrentAction(actionKey) {
        return axiosWrap(GET,
            system.QD_API_URL + "questionnaire-action-templates/" + actionKey + "/current")
            .then((response) => {
                return response.data;
            });
    }


    static saveActionTemplate(actionTemplateObject) {
        return axiosWrap(PUT, system.QD_API_URL + "questionnaire-action-templates/" + actionTemplateObject.templateKey
            , actionTemplateObject)
            .then((response) => {
                return response.data;
            });
    }


    static createActionTemplate(actionTemplateObject) {
        return axiosWrap(POST, system.QD_API_URL + "questionnaire-action-templates"
            , actionTemplateObject)
            .then((response) => {
                return response.data;
            });
    }


    //</editor-fold>
    // -----------------------------------------------------------------------------------------------------------------

    static getActionTypes() {
        return axiosWrap(GET, system.QD_API_URL + `questionnaire-action-types`)
            .then((response) => {
                return response.data;
            })
            .catch((error) => {
                return error;
            });
    }


    static getSettings() {
        return axiosWrap(GET, system.QD_API_URL + `settings`)
            .then((response) => {
                return response.data;
            })
            .catch((error) => {
                return error;
            });
    }


    static getTemplateNotes(id){
        return axiosWrap(GET, system.QD_API_URL + `questionnaire-templates/${id}/notes`)
            .then((response) => {
                return response.data;
            });
    }

    static getAllTemplateNotes(key){
        return axiosWrap(GET, system.QD_API_URL + `questionnaire-templates/key/${key}/notes`)
            .then((response) => {
                return response.data;
            });
    }


    static getQuestionnaireStatistics(id){
        return axiosWrap(GET, system.QD_API_URL + `questionnaire-reference/statistic/${id}`)
            .then((response) => {
                return response.data;
            });
    }


    static getQuestionnaireStatisticsAll(templateKey){
        return axiosWrap(GET, system.QD_API_URL + `questionnaire-reference/statistic/aggregate/${templateKey}`)
            .then((response) => {
                return response.data;
            });
    }


    static getTemplateExport(template){
        return axiosWrap(GET, system.QD_API_URL + `questionnaire-templates/${template.id}/xml`, {responseType: 'blob'})
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', template.name + '.xml');
                link.click();
            });
    }


    static checkTemplateNameAndQualifierExists(templateType, name, nameQualifier) {

        let url = `${system.QD_API_URL}${templateApiMapper[templateType]}/exists?`;

        if (nameQualifier === "") {
            url = url + encodeURI(`exactName=${name}`);
        } else {
            url = url + encodeURI(`exactName=${name}&nameQualifier=${nameQualifier}`);
        }

        return axiosWrap(GET, url)
            .then((response) => {
                return response.data;
            });
    }


    static checkQuestionExists(filter) {
        let url = `${system.QD_API_URL}question-templates/exists?`;
        if (filter.templateKey) {
            url = url + `templateKey=${filter.templateKey}&`;
        }
        if (filter.name) {
            url = url + encodeURI(`exactName=${filter.name}&`);
        }

        return axiosWrap(GET, url)
            .then((response) => {
                return response.data;
            });
    }

    static checkQuestionCategoryExists(filter) {
        let url = `${system.QD_API_URL}question-category-templates/exists?`;
        if (filter.templateKey) {
            url = url + `templateKey=${filter.templateKey}&`;
        }
        if (filter.title) {
            url = url + encodeURI(`exactName=${filter.title}&`);
        }

        return axiosWrap(GET, url)
            .then((response) => {
                return response.data;
            });
    }

    static checkQuestionGroupExists(filter) {
        let url = `${system.QD_API_URL}question-group-templates/exists?`;
        if (filter.templateKey) {
            url = url + `templateKey=${filter.templateKey}&`;
        }
        if (filter.title) {
            url = url + encodeURI(`exactName=${filter.title}&`);
        }

        return axiosWrap(GET, url)
            .then((response) => {
                return response.data;
            });
    }

    // -----------------------------------------------------------------------------------------------------------------
    // TEMPLATE GENERIC !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! <editor-fold>
    // -----------------------------------------------------------------------------------------------------------------
//  + FormatPageableAndFilter(pageable, filter, mapper)
    // Get list of templates (NOTE: This is used for Tags and Integrations)
    static getTemplateList(pageable, filter, templateType) {

        const mapper = templateType === 'question' ? columnNameMapper : columnNameMapper2;

        let str = "";

        // Get all actions when pageable is NOT null
        if (pageable !== null) {
            str = FormatPageableAndFilter(pageable, filter, mapper);
        }

        return axiosWrap(GET, system.QD_API_URL + templateApiMapper[templateType] + str)
            .then((response) => {
                return response.data;
            })
            .catch((error) => {
                return error;
            });
    }

    // Get list of templates that are current (aka latest)
    static getTemplateListCurrent(pageable, filter, templateType) {

        // When getting items for a Select Existing DDLB the filter mode contains the templateType
        let whichTemplateType = (filter.mode === "") ? templateType : filter.mode;

        const mapper = templateType === 'question' ? columnNameMapper : columnNameMapper2;
        let str = "";

        // Get all actions when pageable is NOT null
        if (pageable !== null) {
            str = FormatPageableAndFilter(pageable, filter, mapper);
        }

        return axiosWrap(GET, system.QD_API_URL + templateApiMapper[whichTemplateType] + "/latest" + str)
            .then((response) => {
                return response.data;
            })
            .catch((error) => {
                return error;
            });
    }

    // Get list of templates that match the specified templateType templateKey (versions aka history)
    static getTemplateListVersions(pageable, filter, templateType, key) {

        let templateKey = (key === undefined) ? filter.templateKey : key;

        const mapper = templateType === 'question' ? columnNameMapper : columnNameMapper2
        return axiosWrap(GET, system.QD_API_URL + templateApiMapper[templateType] + "/"+ templateKey + "/history" + FormatPageableAndFilter(pageable, filter, mapper))
            .then((response) => {
                return response.data;
            })
            .catch((error) => {
                return error;
            });
    }


    static getTemplateReferences(templateType, id) {
        return axiosWrap(GET, system.QD_API_URL + templateApiMapper[templateType] + "/" + id + "/reference")
            .then((response) => {
                return response.data;
            })
            .catch((error) => {
                return error;
            });
    }

    //</editor-fold>
    // -----------------------------------------------------------------------------------------------------------------

    // -----------------------------------------------------------------------------------------------------------------
    // MISC !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! <editor-fold>
    // -----------------------------------------------------------------------------------------------------------------

    static updateScoring(scoringUpdateObj) {
        return axiosWrap(PUT, system.QD_API_URL + "questionnaire-templates/scoring", scoringUpdateObj)
            .then((response) => {
                return response.data;
            })
            .catch((error) => {
                return error;
            });
    }

    //</editor-fold>
    // -----------------------------------------------------------------------------------------------------------------
}

TemplateApi.defaultProps = {};

export default TemplateApi;
